import React, { useRef, useState, useEffect, useCallback } from "react"
import { makeStyles } from "makeStyles"

import FloatingPrice from "containers/ProductDetailPage/PriceComponents/FloatingPrice"
import Scrollspy from "react-scrollspy"

const useStyles = makeStyles()({
	responsive: {
		position: "sticky",
		display: "flex",
		justifyContent: "center",
		zIndex: 998,
		top: 0,
		backgroundColor: "white",
		borderBottom: "1px solid whitesmoke",
		borderTop: "1px solid whitesmoke",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		fontSize: 20,
		textAlign: "center",
		"@media only screen and (max-width: 800px)": {
			position: "relative",
			display: "flex",
			justifyContent: "left",
			overflowX: "scroll",
			textAlign: "left",
			width: "100%",
		},
	},
	button: {
		border: "1px solid #B8BCBF",
		borderRadius: 30,
		padding: 5,
		paddingLeft: 16,
		paddingRight: 16,
		marginRight: 14,
		textTransform: "inherit",
		fontWeight: 800,
		fontSize: 14,
		whiteSpace: "nowrap",
		textDecoration: "none",
	},
	scrollSpy: {
		"& $button": {
			background: "#4691CE",
			color: "white",
		},
	},
})

const getNavigationLinks = (
	hasDescription: boolean,
	hasTechnicalDetails: boolean,
	hasAssets: boolean,
) => {
	const navigationLinks: { name: string; anchor: string; id: string }[] = []

	if (hasDescription) {
		navigationLinks.push({
			name: "Beschreibung",
			anchor: "#description",
			id: "description",
		})
	}

	if (hasTechnicalDetails) {
		navigationLinks.push({
			name: "Technische Daten",
			anchor: "#technical-details",
			id: "technical-details",
		})
	}

	navigationLinks.push({
		name: "Bewertungen",
		anchor: "#ratings",
		id: "ratings",
	})

	if (hasAssets) {
		navigationLinks.push({
			name: "Dokumente",
			anchor: "#media",
			id: "media",
		})
	}

	return navigationLinks
}

const StickyBTFNavigation = ({
	singleGrossPriceCents,
	singleGrossMsrpPriceCents,
	addToCart,
	quantity,
	isLoading,
	setQuantity,
	hasAssets,
	hasTechnicalDetails,
	hasDescription,
	addToShoppingList,
	manufacturer,
}: Props) => {
	const { classes } = useStyles()
	const [showPriceComponent, setShowPriceComponent] = useState(false)

	const ref = useRef(null)
	const handleScroll = useCallback(() => {
		if (ref?.current) {
			const topOffset = ref?.current?.getBoundingClientRect()?.top

			if (!showPriceComponent && topOffset === 0) {
				setShowPriceComponent(true)
			}
			if (showPriceComponent && topOffset > 0) {
				setShowPriceComponent(false)
			}
		}
	}, [showPriceComponent])

	useEffect(() => {
		window.addEventListener("scroll", handleScroll)
		return () => {
			window.removeEventListener("scroll", handleScroll)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleScroll])

	const links = getNavigationLinks(hasDescription, hasTechnicalDetails, hasAssets)

	return (
		<>
			<div className={classes.responsive} ref={ref}>
				<Scrollspy
					items={links.map((link) => link.id)}
					currentClassName={classes.scrollSpy}
					style={{ width: 600, display: "flex", justifyContent: "center" }}
				>
					{links.map((link) => {
						const { name, anchor } = link
						return (
							<a
								key={name}
								href={anchor}
								style={{ textDecoration: "none", color: "#505E5F" }}
							>
								<div className={classes.button}>{name}</div>
							</a>
						)
					})}
				</Scrollspy>
				<div>
					{showPriceComponent && (
						<FloatingPrice
							singleGrossPriceCents={singleGrossPriceCents}
							singleGrossMsrpPriceCents={singleGrossMsrpPriceCents}
							addToCart={addToCart}
							isLoading={isLoading}
							quantity={quantity}
							setQuantity={setQuantity}
							addToShoppingList={addToShoppingList}
							variant="sticky-nav"
							manufacturer={manufacturer}
						/>
					)}
				</div>
			</div>
		</>
	)
}

interface Props {
	singleGrossPriceCents: number
	singleGrossMsrpPriceCents: number
	setQuantity: (quantity: number) => void
	addToCart: () => void
	addToShoppingList: () => void
	isLoading: boolean
	quantity: number
	hasTechnicalDetails: boolean
	hasAssets: boolean
	hasDescription: boolean
	manufacturer: string
}

export default StickyBTFNavigation
